import type { ReactNode } from 'react';
import { useState } from 'react';

import { Typography } from '@remarkable/ark-web';
import { useNavigate } from '@tanstack/react-router';

import {
  useEnterpriseCancelEnrollment,
  useEnterpriseEnrollmentStatus,
} from 'src/api/queries/enterpriseEnrollment';
import { Button, Modal, NotificationBox, Tooltip } from 'src/components';

export function CancelSetupModal(props: {
  trigger: ReactNode;
  componentLocation: string;
  disableTooltip?: boolean;
}) {
  const navigate = useNavigate();
  const enrollmentStatus = useEnterpriseEnrollmentStatus();
  const cancelEnrollment = useEnterpriseCancelEnrollment();
  const [open, setOpen] = useState(false);

  if (enrollmentStatus.isSuccess && !enrollmentStatus.data) {
    // Enrollment not yet initialised
    return null;
  }

  return (
    <Modal.Root open={open} onOpenChange={(newState) => setOpen(newState)}>
      <Tooltip title="Cancel setup" asChild enabled={!props.disableTooltip}>
        <Modal.Trigger asChild>{props.trigger}</Modal.Trigger>
      </Tooltip>

      <Modal.Content
        className="flex flex-col gap-24"
        title="Cancel enterprise setup?"
      >
        <Typography variant="body-md">
          If you cancel the setup, all the information entered will be lost.
        </Typography>

        <div className="flex gap-16 self-center">
          <Modal.Close asChild>
            <Button variant="tertiary">Close</Button>
          </Modal.Close>

          <Button
            disabled={cancelEnrollment.isPending}
            loading={cancelEnrollment.isPending}
            analytics={{
              action: 'cancel enterprise enrollment',
              text: 'Cancel setup',
              location: props.componentLocation,
            }}
            onClick={() => {
              cancelEnrollment.mutate(undefined, {
                onSuccess() {
                  setOpen(false);

                  return navigate({ to: '/enterprise/enroll/setup' });
                },
              });
            }}
          >
            Cancel setup
          </Button>
        </div>

        <NotificationBox error={cancelEnrollment.error} />
      </Modal.Content>
    </Modal.Root>
  );
}
